import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const api = {
  // Search providers
  searchProviders: async (params) => {
    const response = await axios.get(`${API}/npi/search`, { params });
    return response.data;
  },

  // Get single provider by NPI
  getProvider: async (npi) => {
    const response = await axios.get(`${API}/npi/provider/${npi}`);
    return response.data;
  },

  // Get single provider with enriched context
  getProviderEnriched: async (npi) => {
    const response = await axios.get(`${API}/npi/provider/${npi}/enriched`, {
      timeout: 60000,
    });
    return response.data;
  },

  // Get related providers for internal linking
  getRelatedProviders: async (npi, params = {}) => {
    const response = await axios.get(`${API}/npi/provider/${npi}/related`, { params });
    return response.data;
  },

  // Get providers by specialty
  getProvidersBySpecialty: async (specialty, params = {}) => {
    const response = await axios.get(`${API}/npi/specialty/${specialty}`, { params });
    return response.data;
  },

  // Get providers by state
  getProvidersByState: async (stateCode, params = {}) => {
    const response = await axios.get(`${API}/npi/state/${stateCode}`, { params });
    return response.data;
  },

  // Get US states list
  getStates: async () => {
    const response = await axios.get(`${API}/states`);
    return response.data;
  },

  // Get featured specialties
  getSpecialties: async () => {
    const response = await axios.get(`${API}/specialties`);
    return response.data;
  },

  // Get recent searches
  getRecentSearches: async (limit = 10) => {
    const response = await axios.get(`${API}/recent-searches`, { params: { limit } });
    return response.data;
  },

  // Get popular providers
  getPopularProviders: async (limit = 10) => {
    const response = await axios.get(`${API}/popular-providers`, { params: { limit } });
    return response.data;
  }
};

// Helper functions
export const getProviderName = (provider) => {
  if (!provider) return '';
  const basic = provider.basic || {};
  if (provider.enumeration_type === 'NPI-1') {
    const firstName = basic.first_name || '';
    const lastName = basic.last_name || '';
    const credential = basic.credential || '';
    return `${firstName} ${lastName}${credential ? ', ' + credential : ''}`.trim();
  } else {
    return basic.organization_name || '';
  }
};

export const getProviderNameOnly = (provider) => {
  if (!provider) return '';
  const basic = provider.basic || {};
  if (provider.enumeration_type === 'NPI-1') {
    return `${basic.first_name || ''} ${basic.last_name || ''}`.trim();
  } else {
    return basic.organization_name || '';
  }
};

export const getCredentials = (provider) => {
  if (!provider) return '';
  return provider.basic?.credential || '';
};

export const getPrimarySpecialty = (provider) => {
  if (!provider?.taxonomies?.length) return '';
  const primary = provider.taxonomies.find(t => t.primary) || provider.taxonomies[0];
  return primary?.desc || '';
};

export const getLocation = (provider) => {
  if (!provider?.addresses?.length) return '';
  const address = provider.addresses.find(a => a.address_purpose === 'LOCATION') || provider.addresses[0];
  return address ? `${address.city || ''}, ${address.state || ''}` : '';
};

export const getFullAddress = (provider) => {
  if (!provider?.addresses?.length) return null;
  const address = provider.addresses.find(a => a.address_purpose === 'LOCATION') || provider.addresses[0];
  if (!address) return null;
  return {
    line1: address.address_1 || '',
    line2: address.address_2 || '',
    city: address.city || '',
    state: address.state || '',
    postalCode: address.postal_code?.substring(0, 5) || '',
    phone: address.telephone_number || '',
    fax: address.fax_number || ''
  };
};

export const getEnumerationDate = (provider) => {
  if (!provider?.basic?.enumeration_date) return '';
  try {
    const date = new Date(provider.basic.enumeration_date);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  } catch {
    return provider.basic.enumeration_date;
  }
};

export const getSpecialtySlug = (specialty) => {
  return specialty.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');
};

export const getNameSlug = (name) => {
  return name.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, '-');
};

export const getSpecialtyColor = (specialty) => {
  const lower = (specialty || '').toLowerCase();
  if (lower.includes('cardio')) return 'specialty-cardiology';
  if (lower.includes('dermat')) return 'specialty-dermatology';
  if (lower.includes('pediat')) return 'specialty-pediatrics';
  if (lower.includes('psychi')) return 'specialty-psychiatry';
  if (lower.includes('orthop')) return 'specialty-orthopedics';
  if (lower.includes('family')) return 'specialty-family-medicine';
  if (lower.includes('internal')) return 'specialty-internal-medicine';
  return 'specialty-default';
};

export const generateGoogleMapsLink = (address) => {
  if (!address) return '#';
  const query = encodeURIComponent(
    `${address.line1} ${address.line2} ${address.city} ${address.state} ${address.postalCode}`.trim()
  );
  return `https://www.google.com/maps/search/?api=1&query=${query}`;
};
